import { useState } from 'react'
import { Building2, Check, ChevronDown, Globe, MessagesSquare, Palette, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useTranslation } from '@/hooks/use-translation'
import { useSetupProgress } from '../hooks/use-setup-progress'
import type { SetupStepId } from '../types/setup-progress.types'
import { SetupProgressItem } from './SetupProgressItem'
import { SetupProgressRing } from './SetupProgressRing'

const STEP_ICONS: Record<SetupStepId, typeof Building2> = {
  websites: Globe,
  companyData: Building2,
  brandIdentity: Palette,
  brandVoice: MessagesSquare,
}

export function SetupProgressWidget() {
  const t = useTranslation()
  const { steps, overallPercentage, isFullyComplete, isLoading, isDismissed, dismiss } =
    useSetupProgress()
  const [showCompleted, setShowCompleted] = useState(false)

  if (isLoading || isFullyComplete || isDismissed) return null

  const incompleteSteps = steps.filter((s) => !s.isComplete)
  const completedSteps = steps.filter((s) => s.isComplete)

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{
          height: { type: 'spring', damping: 30, stiffness: 300 },
          opacity: { duration: 0.2 },
        }}
        className="mx-3 mb-2"
      >
        <div className="bg-white/80 backdrop-blur-sm border border-primary-100 rounded-xl p-3 shadow-sm">
          {/* Header */}
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2.5">
              <SetupProgressRing percentage={overallPercentage} size={34} strokeWidth={3} />
              <div>
                <span className="text-xs font-semibold text-neutral-900">
                  {t('setupProgress.title')}
                </span>
                <p className="text-[10px] text-neutral-500">
                  {t('setupProgress.percentComplete', { percent: overallPercentage })}
                </p>
              </div>
            </div>
            <button
              onClick={dismiss}
              className="p-1 rounded-md text-neutral-400 hover:text-neutral-600 hover:bg-neutral-200/60 transition-smooth"
              aria-label={t('setupProgress.dismiss')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-primary-100 to-transparent mb-1" />

          {/* Incomplete steps */}
          <div className="space-y-0">
            {incompleteSteps.map((step) => (
              <SetupProgressItem
                key={step.id}
                step={step}
                label={t(`setupProgress.steps.${step.id}.label`)}
                description={t(`setupProgress.steps.${step.id}.description`)}
                icon={STEP_ICONS[step.id]}
              />
            ))}
          </div>

          {/* Completed steps toggle */}
          {completedSteps.length > 0 && (
            <div className="mt-1">
              <button
                onClick={() => setShowCompleted((prev) => !prev)}
                className="flex items-center gap-1.5 px-2 py-1 w-full text-[10px] text-neutral-400 hover:text-neutral-600 transition-smooth"
              >
                <div className="flex items-center gap-1">
                  <Check className="h-3 w-3 text-success-500" />
                  <span>{completedSteps.length} {t('setupProgress.completed')}</span>
                </div>
                <ChevronDown
                  className={`h-3 w-3 transition-smooth ${showCompleted ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {showCompleted && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { type: 'spring', damping: 30, stiffness: 300 },
                      opacity: { duration: 0.15 },
                    }}
                    className="overflow-hidden"
                  >
                    {completedSteps.map((step) => (
                      <SetupProgressItem
                        key={step.id}
                        step={step}
                        label={t(`setupProgress.steps.${step.id}.label`)}
                        description={t(`setupProgress.steps.${step.id}.description`)}
                        icon={STEP_ICONS[step.id]}
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}
